import React, { useEffect, useState } from "react";
import { Box, Card, CardContent, Tooltip, Typography } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import HdrAutoIcon from "@mui/icons-material/HdrAuto";
import { Base } from "../../framework/common/base";
import { LinkIconButton } from "../../framework/common/linkButton";
import { generatePath, useParams } from "react-router-dom";
import { DayBookingState, StrMeasureUnit } from "../../models/common/enums";
import { DayBookingEntry } from "../../models/dayBooking/dayBooking";
import { useAppSelector } from "../../framework/redux/hooks";
import { DayBookingType } from "../../models/dayBookingType/dayBookingType";
import { getDurationBasedOnUnit } from "../../models/dayBooking/dayBookingHelpers";
import { useTranslation } from "react-i18next";
import { DayBookingFormLocationState } from "../dayBooking/dayBookingFormMain";

interface IDayBookingEntryCardProps {
    item: DayBookingEntry;
    handleDelete: (id: string) => void;
}

const costCenterInfoArray = (item: DayBookingEntry) => {
    const formattedArray = [
        item.dayBookingTypeName,
        item.costCenterName,
        item.vehicleName,
    ]
        .filter((value) => value)
        .join(", ");
    return formattedArray;
};

const formatEndDay = (item: DayBookingEntry) =>
    // Don't display end day for NotFullDay bookings
    item.day === item.endDay ? "" : `  - ${Base.dateToDateStr(item.endDay)}`;

export const DayBookingEntryCard = ({
    item,
    handleDelete,
}: IDayBookingEntryCardProps) => {
    const dayBookingTypes = useAppSelector(
        ({ dayBooking }) => dayBooking.dayBookingTypes
    );
    const [dayBookingType, setDayBookingType] = useState<DayBookingType>();
    const { date } = useParams();

    const canEditWorkEntries = useAppSelector(
        ({ session }) => session.employeeGroupParameters?.canEditWorkEntries
    );
    const canAddAndEditDayBookings = useAppSelector(
        ({ session }) => session.employeeGroupParameters?.canAddAndEditDayBookings
    );

    //  set selected dayBookingType
    useEffect(() => {
        if (dayBookingTypes) {
            const dayBookingType = dayBookingTypes.find(
                (dayBookingType) => dayBookingType.id === item.dayBookingTypeId
            );
            setDayBookingType(dayBookingType);
        }
    }, [dayBookingTypes, item]);

    const canEdit = item.state === DayBookingState.UnLocked &&
        canEditWorkEntries &&
        canAddAndEditDayBookings;
    const canDelete = item.state !== DayBookingState.Locked &&
        canEditWorkEntries &&
        canAddAndEditDayBookings;
    const onDelete = () => handleDelete(item.id);

    if (!dayBookingType) return null;

    return (
        <Card sx={{ mb: 2, bgcolor: "lightGrayBlue.main" }}>
            <CardContent>
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                    }}
                >
                    <Box
                        sx={{
                            textAlign: "left",
                        }}
                    >
                        <Typography
                            variant="h5"
                            color="primary.dark"
                            gutterBottom
                        >
                            {`${Base.dateToDateStr(item.day)}${formatEndDay(
                                item
                            )}`}
                            <AutomaticDailyAllowanceIndicator item={item} />
                        </Typography>
                    </Box>
                    <Box
                        sx={{
                            textAlign: "right",
                            whiteSpace: "nowrap",
                        }}
                    >
                        <Typography variant="h5" color="success.main">
                            {getDurationBasedOnUnit(item, dayBookingType)}{" "}
                            {dayBookingType?.salaryRowTypeMeasureUnit ??
                                StrMeasureUnit.Day}
                        </Typography>
                    </Box>
                </Box>
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-end",
                    }}
                >
                    <Box
                        sx={{
                            textAlign: "left",
                        }}
                    >
                        <Typography variant="body2" color="primary.dark">
                            {costCenterInfoArray(item)}
                        </Typography>
                    </Box>
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "flex-end",
                            alignItems: "center",
                        }}
                    >
                        {canEdit && (
                            <LinkIconButton
                                to={generatePath(
                                    "/work/form/daybooking/edit/:id/:date?",
                                    {
                                        id: item.isAutomatic
                                            ? "automatic"
                                            : item.id,
                                        date,
                                    }
                                )}
                                state={
                                    item.isAutomatic
                                        ? ({
                                            saveData: {
                                                dayBookingTypeId:
                                                      item.dayBookingTypeId,
                                                costCenterId:
                                                      item.costCenterId,
                                                vehicleId: item.vehicleId,
                                                day: item.day,
                                                endDate: item.endDay,
                                                amount: item.amount,
                                                dayUsageType:
                                                      item.dayUsageType,
                                                comment: item.comment,
                                                automaticCategory:
                                                      item.id.split(".")[1],
                                                automaticWorkShiftTimeSlotId:
                                                      item.workShiftTimeSlotId,
                                            },
                                        } as DayBookingFormLocationState)
                                        : undefined
                                }
                            >
                                <EditIcon color="primary" />
                            </LinkIconButton>
                        )}
                        {canDelete ? (
                            <DeleteForeverIcon
                                color="error"
                                onClick={onDelete}
                            />
                        ) : null}
                    </Box>
                </Box>
            </CardContent>
        </Card>
    );
};

interface AutomaticDailyAllowanceIndicatorProps {
    item: DayBookingEntry;
}

const AutomaticDailyAllowanceIndicator = ({
    item,
}: AutomaticDailyAllowanceIndicatorProps) => {
    const { t } = useTranslation();

    if (!item.isAutomatic) {
        return null;
    }
    return (
        <Box
            sx={{
                ml: 1,
                display: "inline-block",
                verticalAlign: "middle",
                pt: 0.25,
            }}
        >
            <Tooltip
                title={t("dayBooking.automatic", {
                    label: item.dayBookingTypeName,
                })}
                enterTouchDelay={0}
                leaveTouchDelay={20000}
            >
                <HdrAutoIcon color="primary" />
            </Tooltip>
        </Box>
    );
};